import { create } from 'zustand';
import type { PhaseType } from '@beat-games/game-config';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface SeasonState {
  phaseType: PhaseType;
  phaseName: string;
  phaseEndTime: string | null;
  timeRemainingMs: number | null;
  totalPlayers: number;
  qualificationLine: number;
  isLoading: boolean;
  lastFetched: number | null;
}

interface SeasonStore extends SeasonState {
  fetchSeasonStatus: () => Promise<void>;
  updateTimeRemaining: () => void;
  reset: () => void;
}

const initialState: SeasonState = {
  phaseType: 'QUALIFIER',
  phaseName: 'Qualifiers',
  phaseEndTime: null,
  timeRemainingMs: null,
  totalPlayers: 0,
  qualificationLine: 256,
  isLoading: false,
  lastFetched: null,
};

export const useSeasonStore = create<SeasonStore>((set, get) => ({
  ...initialState,

  fetchSeasonStatus: async () => {
    set({ isLoading: true });

    try {
      const response = await fetch(`${API_BASE}/api/season/status`);
      const data = await response.json();

      if (data.success && data.data) {
        set({
          phaseType: data.data.phaseType || 'QUALIFIER',
          phaseName: data.data.phaseName || 'Qualifiers',
          phaseEndTime: data.data.phaseEndTime || null,
          timeRemainingMs: data.data.timeRemainingMs ?? null,
          totalPlayers: data.data.totalPlayers ?? 0,
          qualificationLine: data.data.qualificationLine ?? 256,
          isLoading: false,
          lastFetched: Date.now(),
        });
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('Failed to fetch season status:', error);
      set({ isLoading: false });
    }
  },

  // Update time remaining based on phaseEndTime (for local countdown)
  updateTimeRemaining: () => {
    const { phaseEndTime } = get();
    if (!phaseEndTime) return;

    const endMs = new Date(phaseEndTime).getTime();
    const remaining = Math.max(0, endMs - Date.now());
    set({ timeRemainingMs: remaining });
  },

  reset: () => set(initialState),
}));
